import { PropsWithChildren, useContext, useEffect, useMemo, useState } from "react";
import { Spinner, ToolbarButton, ToolbarDivider, ToolbarGroup, makeStyles, shorthands, tokens } from "@fluentui/react-components";
import {
    InfoRegular,
    FilterRegular
} from "@fluentui/react-icons";
import { TeamsFxContext } from "context/teams-context";
import { usePPA } from "context/ppa-context";
import { useL10n } from "context/l10n-context";
import { ILocaleStrings } from "loc/locale.interfaces";
import { IReportRequestsProps } from "./report-requests.props";
import CommandBar from "components/command-bar/command-bar";
import { IReportRequest } from "@interfaces/reports/reports-common.interfaces";
import { IApiError, ICommandBarItem, ITableColumn } from "@interfaces/common.interfaces";
import NavigateCell from "../report-results-grid-cells/navigate-cell/navigate-cell";
import IconWithLabel from "../report-results-grid-cells/common/icon-with-label/icon-with-label";
import { getReportRequestStatusIcon } from "lib/report-requests";
import PercentageBar from "components/common/percentage-bar/percentage-bar";
import PrincipalsCell from "../report-results-grid-cells/principals-cell/principals-cell";
import DateTimeCell from "../report-results-grid-cells/date-time-cell/date-time-cell";
import { BearerTokenAuthProvider, createApiClient } from "@microsoft/teamsfx";
import Grid from "components/grid/grid";
import ReportRequestsGrid from "../report-requests-grid/report-requests-grid";
import ScrollPanel from "components/layout/scroll-panel/scroll-panel";
import { CallbackButton } from "components/command-bar/callback-button/callback-button";
import ApiErrorBar from "components/common/api-error-bar/api-error-bar";
import { getError } from "lib/utils";

const defaultCommandBarItems: Array<ICommandBarItem> = [
    {
        id: 'openFilter',
        placement: 'right',
        order: 1,
        component: <CallbackButton icon={<FilterRegular />} disabled />
    }
];

const layoutStyles = makeStyles({
    mainWrapper: {
        //...shorthands.borderWidth('1px', '0'),
    },
    spinner: {
        ...shorthands.margin('2em', 0)
    }
});

export default function ReportRequests(props: IReportRequestsProps) {
    const styles = layoutStyles();
    const { t } = useL10n<ILocaleStrings>();
    const { getApiClient } = usePPA();

    const [loading, setLoading] = useState<boolean>(true);
    const [apiError, setApiError] = useState<IApiError | null>(null);
    const [reportRequests, setReportRequests] = useState<Array<IReportRequest>>([]);
    const [checkCount, setCheckCount] = useState<number>(0);

    const getReportRequests = async (): Promise<Array<IReportRequest>> => {
        try {
            const response = await getApiClient().get<Array<IReportRequest>>(`/api/report-requests`);
            setCheckCount(checkCount + 1);
            return response.data;
        }
        catch (ex: unknown) {
            throw ex;
        }
    }

    useEffect(() => {
        setLoading(true);
        getReportRequests().then(
            (data: Array<IReportRequest>) => {
                setReportRequests(data);
                setLoading(false);
            },
            (error: unknown) => {
                setLoading(false);
                setApiError(getError(error));
            });
    }, []);

    useEffect(() => {
        // 
        var timerId: NodeJS.Timeout;
        const hasProcessingRequests: boolean = reportRequests.filter(r => r.status !== 3 && r.status !== 4).length > 0;
        const timeOut = hasProcessingRequests ? 2000 : 10000;

        timerId = setTimeout(() => {
            getReportRequests().then(
                (data: Array<IReportRequest>) => {
                    setReportRequests(data);
                },
                (error: unknown) => {
                    setApiError(getError(error));
                });
        }, timeOut);

        return () => clearTimeout(timerId);
    }, [checkCount]);


    return (
        <ScrollPanel
            fixed={<CommandBar items={defaultCommandBarItems} />}
            scroll={<>
                {loading &&
                    <Spinner
                        className={styles.spinner}
                        labelPosition="below"
                        label={t('loading')} />}
                {!loading &&
                    <>
                        <ReportRequestsGrid
                            items={reportRequests}
                            hasApiError={apiError !== null}
                            onSelectionChanged={(items: Array<IReportRequest>) => { }} />
                        <ApiErrorBar error={apiError} />
                    </>}
            </>}
        />
    );
}